import { useEffect, useMemo, useState } from "react";
import { type FeatureFlags, FEATURE_FLAG_DEFINITIONS } from "../../config/featureFlags";
import { evaluateFlag } from "../../clients/launchDarkly";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { handleError } from "../../errors";
import Logger from "../../clients/sentry";

export function useFeatureFlag<T extends keyof FeatureFlags>(featureFlag: T): FeatureFlags[T] | undefined {
  const ldClient = useLDClient();
  const [flagValue, setFlagValue] = useState<FeatureFlags[T] | undefined>(() => evaluateFlag(featureFlag));

  // Streaming Handler
  useEffect(() => {
    if (!ldClient || !FEATURE_FLAG_DEFINITIONS[featureFlag]?.streaming) return;

    const handleFlagChange = () => {
      setFlagValue(evaluateFlag(featureFlag));
    };

    ldClient.on(`change:${featureFlag}`, handleFlagChange);

    return () => ldClient.off(`change:${featureFlag}`, handleFlagChange);
  }, [featureFlag, ldClient]);

  // Initialization
  useEffect(() => {
    if (!ldClient) return;

    (async () => {
      try {
        await ldClient.waitForInitialization(2);
      } catch (err) {
        const error = handleError(err, {
          context: "useFeatureFlag",
          featureFlag,
        });
        Logger.error("LaunchDarkly failed to initialize", error);
      } finally {
        setFlagValue(evaluateFlag(featureFlag));
      }
    })();
  }, [featureFlag, ldClient]);

  return flagValue;
}

export function useReportOptionFeatureFlags(): Partial<Record<keyof FeatureFlags, unknown>> {
  return useMemo(() => ({}), []);
}
